import { Dispatch } from 'redux';
import { logger } from '../utils/logger/logger';
import {
    DatabaseId,
    VerificationResponse,
    ViewModel,
    BoundAction,
    BoundActionCreator,
    FieldValidationErrors,
    VerificationStep,
    ProgramTheme,
    Messages,
    Locale,
    Segment,
    ProgramThemeMessages,
    DocUploadViewModel,
    Organization,
} from '../types/types';
import { assertValidProgramId } from '../types/assertions';
import { VerificationApiClient } from '../ServerApi/VerificationApiClient';
import { handleSubmitResponse, initViewModel, clearUploadedFiles } from './VerificationServiceHelpers';
import { getHook } from '../hooks/hooks';
import { getMessages, getLocaleSafely } from '../intl/intl';
import { getOptions } from '../../options/options';
import { getThemeMessages, getMetadataConfig } from '../ProgramTheme/programThemeGetters';
import { initCodeSplitBaseUrl } from '../utils/routing/Url';
import { saveVerificationIdForConversion } from '../conversion/conversion';
import { scrollToWindowTop } from '../utils/browser/windowHelpers';
import { getFingerprint } from '../fingerprint/fingerprint';
import { doViewModelReset } from './ViewModel';
import { deepClone } from '../utils/objects';
import { getMetadataFromUrl } from '../metadata/metadata';
import { fetchProgramOrganizations } from '../ServerApi/OrganizationApiClient';
import { SegmentEnum } from '../types/runtimeTypes';

/**
 * @description Start a new verification "flow"
 * @param dispatch Redux action dispatch
 */
export const createFetchNewVerificationRequest: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    programId: DatabaseId,
    segment: Segment,
    previousViewModel?: ViewModel,
    trackingId?: string,
) => {
    assertValidProgramId(programId);
    await dispatch({ type: 'IS_LOADING', isLoading: true });
    try {
        const verificationResponsePromise = VerificationApiClient.fetchNewVerificationRequest(programId, trackingId);
        return createFetchVerificationRequest({
            dispatch,
            verificationResponsePromise,
            programId,
            segment,
            previousViewModel,
        });
    } catch (e) {
        logger.error(e);
    }
};

/**
 * @description Provided with a verificationId, continue verifying someone.
 * @param dispatch Redux action dispatch
 */
export const createFetchExistingVerificationRequest: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    programId: DatabaseId,
    verificationId: string,
    previousVerificationResponse?: VerificationResponse,
    previousViewModel?: ViewModel,
) => {
    assertValidProgramId(programId);
    await dispatch({ type: 'IS_LOADING', isLoading: true });

    try {
        const verificationResponsePromise = VerificationApiClient.fetchExistingVerificationRequest(verificationId);
        return createFetchVerificationRequest({
            dispatch,
            verificationResponsePromise,
            programId,
            previousViewModel,
            previousVerificationResponse,
        });
    } catch (e) {
        logger.error(e);
    }
};

export const segmentChanged = (currentSegment: Segment, nextSegment: Segment): boolean => {
    if (!nextSegment) {
        return false;
    }
    return !!(currentSegment && currentSegment !== nextSegment);
};

export const currentStepChanged = (previousVerificationResponse: VerificationResponse, verificationResponse: VerificationResponse): boolean =>
    !previousVerificationResponse || previousVerificationResponse.currentStep !== verificationResponse.currentStep;

/**
 * @description Await several items (new/existing verification request, program theme, etc) then update central state (redux).
 * Timing and order is very important within this function as it is bootstrapping most of the application.
 *
 * @param dispatch Redux action dispatch
 * @param verificationResponsePromise Either a new or existing verification server request.
 * @param programId
 * @param previousViewModel Optional
 * @private
 */
const createFetchVerificationRequest = async ({
    dispatch,
    verificationResponsePromise,
    programId,
    segment,
    previousViewModel,
    previousVerificationResponse,
}: {
    dispatch: Dispatch;
    programId: DatabaseId;
    verificationResponsePromise: Promise<VerificationResponse>;
    segment?: Segment;
    previousViewModel?: ViewModel;
    previousVerificationResponse?: VerificationResponse;
}): Promise<any> => {
    initCodeSplitBaseUrl();

    try {
        const fingerprintPromise: Promise<string> = getFingerprint();
        const programThemePromise: Promise<ProgramTheme> = VerificationApiClient.fetchProgramTheme(programId);
        const programTheme: ProgramTheme = await programThemePromise;
        const verificationResponse: VerificationResponse = await verificationResponsePromise;
        logger.info('verificationResponse received: ', verificationResponse);

        const previousLocale = (previousViewModel && previousViewModel.localeChoice ? previousViewModel.localeChoice.value : undefined);
        const locale = previousLocale || programTheme.intl.locale || getOptions().locale;
        const messagesPromise: Promise<Messages> = getMessages(locale, programTheme.intl.messages, verificationResponse.segment);

        const nextSegment = verificationResponse.segment;

        let orgList: Organization[] = null;
        if (nextSegment === SegmentEnum.MILITARY) {
            const orgListPromise: Promise<Organization[]> = fetchProgramOrganizations(programTheme.config.orgSearchUrl, '');
            orgList = await orgListPromise;
            logger.info('orgList received: ', orgList);
        }

        clearUploadedFiles(previousViewModel as DocUploadViewModel); // Need to remove the file elements from state since deepClone cannot clone them

        let updatedPreviousViewModel = deepClone(previousViewModel);
        if (segmentChanged(segment, nextSegment)) {
            logger.info('segment changed: ', segment, nextSegment);
            await doViewModelReset(dispatch)(verificationResponse);
            updatedPreviousViewModel = undefined;
        }

        const fingerprint = await fingerprintPromise;
        const currentStep = verificationResponse.currentStep;
        const viewModel = initViewModel({ currentStep, fingerprint, previousViewModel: updatedPreviousViewModel });

        if (currentStepChanged(previousVerificationResponse, verificationResponse)) {
            getHook('ON_VERIFICATION_STEP_CHANGE')(verificationResponse);
        }

        saveVerificationIdForConversion(verificationResponse.verificationId);

        if (viewModel) {
            viewModel.metadata = { ...viewModel.metadata, ...getMetadataFromUrl(getMetadataConfig(programTheme).keys) };
        }

        const messages: Messages = await messagesPromise;

        return Promise.all([
            dispatch({ verificationResponse, type: 'VERIFICATION_RESPONSE' }),
            dispatch({ viewModel, type: 'VIEW_MODEL' }),
            dispatch({ programTheme, type: 'PROGRAM_THEME' }),
            dispatch({ messages, locale, type: 'SET_MESSAGES' }),
            dispatch({ type: 'IS_LOADING', isLoading: false }),
            dispatch({ orgList, type: 'PRE_LOAD_ORGS' }),
        ])
        .then(() => {
            getHook('ON_VERIFICATION_READY')(verificationResponse);
        });
    } catch (e) {
        logger.error(e);
        throw(e);
    }
};

/**
 * @description Action creator to update the view model in redux.
 * @param dispatch
 */
export const createUpdateViewModel: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    viewModel: ViewModel,
) => dispatch({
    viewModel,
    type: 'VIEW_MODEL',
});

export const createUpdateLocale: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    viewModel: ViewModel,
    programTheme: ProgramTheme,
    segment: Segment,
): Promise<object> => {
    const locale: Locale = getLocaleSafely(viewModel);
    const themeMessages: ProgramThemeMessages = getThemeMessages(programTheme);
    return getMessages(locale, themeMessages, segment)
        .then((messages: Messages) => dispatch({ messages, viewModel, type: 'VIEW_MODEL' }));
};

/**
 * @description Action creator to update the program theme in redux.
 * @param dispatch
 */
export const createUpdateProgramTheme: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    programTheme: ProgramTheme,
) => dispatch({ programTheme, type: 'PROGRAM_THEME' });

/**
 * @description Action creator to update the field validation errors, which are those that are shown to a user as they fill-in the HTML form.
 * @param dispatch
 */
export const createUpdateFieldValidationErrors: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (
    fieldValidationErrors: FieldValidationErrors,
) => dispatch({ fieldValidationErrors, type: 'FIELD_VALIDATION_ERRORS' });

/**
 * @description Action creator to submit a step to the backend REST API
 * @param dispatch
 */
export const createSubmitStep: BoundActionCreator = (
    dispatch: Dispatch,
): BoundAction => async (stepName: VerificationStep, viewModel: ViewModel, previousResponse: VerificationResponse) => {
    logger.log(`submitStep(): submitting stepName ${stepName}, viewModel:`, viewModel);
    scrollToWindowTop();
    await dispatch({ type: 'IS_LOADING', isLoading: true });
    logger.info(`submitting step ${stepName}`, viewModel);
    const verificationResponse: VerificationResponse = await VerificationApiClient.submitStep(stepName, previousResponse, viewModel);
    const newViewModel = handleSubmitResponse(verificationResponse, viewModel);

    const promiseVerificationResponse = dispatch({ verificationResponse, type: 'VERIFICATION_RESPONSE' });
    const promiseViewModel = dispatch({ viewModel: newViewModel, type: 'VIEW_MODEL' });
    const promiseIsLoading = dispatch({ type: 'IS_LOADING', isLoading: false });

    getHook('ON_VERIFICATION_STEP_CHANGE')(verificationResponse);

    return Promise.all([promiseVerificationResponse, promiseViewModel, promiseIsLoading]);
};
